import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EndorsementStatusClient, EndorsementStatusDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getApiClient = () => new EndorsementStatusClient(API_BASE_URL, axiosInstance);

export const useEndorsementStatuses = () =>
  useQuery({
    queryKey: ['endorsementStatuses'],
    queryFn: async () => {
      const response = await getApiClient().searchEndorsementStatuses({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

export const useEndorsementStatusSearch = () => {
  const search = async (params: any) => {
    const response = await getApiClient().searchEndorsementStatuses(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

export const useEndorsementStatusById = (id: string | undefined) =>
  useQuery({
    queryKey: ['endorsementStatus', id],
    queryFn: async () => {
      const response = await getApiClient().getEndorsementStatusById(id!);
      return response?.data || null;
    },
    enabled: !!id,
  });

export const useCreateEndorsementStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EndorsementStatusDTO) => getApiClient().createEndorsementStatus(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementStatuses'] }),
  });
};

export const useUpdateEndorsementStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EndorsementStatusDTO }) =>
      getApiClient().updateEndorsementStatus(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementStatuses'] }),
  });
};

export const useDeleteEndorsementStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getApiClient().deleteEndorsementStatus(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['endorsementStatuses'] }),
  });
};